/**
 * personalizacao-preview.js — Fanaticos Por Mantos
 * v7 (revert v5 sem badge) — SRC SWAP da imagem visível.
 *
 * Quando "Personalizar" é selecionado:
 *   1. Pega a imagem ativa do carousel
 *   2. Salva tudo (src, data-src, data-zoom, srcset, class)
 *   3. Substitui pela camisa em branco (brasilcostastitular.webp)
 *   4. Insere overlay (nome + número PNGs) dentro do parent
 *
 * Quando "Não Personalizar":
 *   1. Restaura a src original
 *   2. Remove overlay
 */

(() => {
  const PERSONALIZATION_HANDLES = new Set([
    /* 3 primeiros que criei (já funcionando) */
    "camisa-home-2026-torcedor",
    "camisa-home-2026-jogador",
    "kit-infantil-2026-home",
    /* 5 que acabei de criar (completam o set Brasil 2026/27 equivalente à MC) */
    "camisa-away-2026-torcedor",
    "camisa-away-2026-jogador-1",
    "camisa-home-2026-feminina",
    "camisa-away-2026-feminina",
    "kit-infantil-2026-away-1",
    /* 5 drafts pré-existentes da Fanaticos (cobertura adicional) */
    "camisa-feminina-copa-do-mundo-2026-26-home-copia",
    "camisa-feminina-copa-do-mundo-2026-26-away-copia",
    "camisa-copa-do-mundo-2026-26-away-copia",
    "camisa-away-2026-jogador",
    "kit-infantil-2026-away",
    /* Corinthians 2026/27 (4 home + 4 away) */
    "camisa-corinthians-2026-27-home",
    "camisa-corinthians-jogador-2026-27-home",
    "camisa-feminina-corinthians-2026-27-home",
    "kit-infantil-corinthians-2026-27-home",
    "camisa-corinthians-2026-27-away",
    "camisa-corinthians-jogador-2026-27-away",
    "camisa-feminina-corinthians-2026-27-away",
    "kit-infantil-corinthians-2026-27-away",
  ]);

  const FEATURE_TAG = "personalizar-preview";
  const KIT_TAG_PREFIX = "personalizar-kit:";

  /* Configuração por time. Detecção pelo handle do produto.
     useLetterPngs=true → renderiza nome com PNGs de letras (igual número).
     useLetterPngs=false → renderiza nome com fonte CSS (texto). */
  const TEAMS = {
    corinthians: {
      detect: (handle) => /corinthians/i.test(handle),
      useLetterPngs: true,
      assetPrefix: "kit-corinthians-",  /* kit-corinthians-home-num-0.png, kit-corinthians-home-letter-A.png */
      fontFamily: null,
      nameColors: { home: "#000000", away: "#FFFFFF" },
    },
    brasil: {
      detect: () => true, /* fallback */
      useLetterPngs: false,
      assetPrefix: "kit-",
      fontFamily: "BrasilNomeKit",
      nameColors: { home: "#003D49", away: "#FFFFFF" },
    },
  };

  const normalize = (s) =>
    (s || "").toString().trim().toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "");

  const imgCache = new Map();
  function preloadImg(url) {
    if (imgCache.has(url)) return imgCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    imgCache.set(url, p);
    return p;
  }

  class FsbPersonalizacao {
    constructor(section) {
      this.section = section;
      this.cfg = this._readCfg();
      this.nomeInput = this._findInput("Nome");
      this.numInput = this._findInput("Número");
      this.aparecerEl =
        section.querySelector("#aparecer") ||
        document.querySelector("#aparecer");
      this.carousel = section.querySelector(".product-gallery__carousel");
      this.swappedImg = null;
      this.overlayDiv = null;
      this.isActive = false;
      this._debounce = null;
    }

    _readCfg() {
      const s = this.section.querySelector("script[data-fsb-personalizacao-config]");
      if (!s) return null;
      try { return JSON.parse(s.textContent); } catch { return null; }
    }

    _findInput(propName) {
      /* Tenta o selector exato (com acento). Se falhar, varre todos os
         inputs properties[…] e compara case-insensitive substring.
         Necessário porque "Número" tem acento que dá problema em alguns
         engines de CSS attribute selector. */
      const exact = `input[name="properties[${propName}]"]`;
      let el = this.section.querySelector(exact) || document.querySelector(exact);
      if (el) return el;

      const lcTarget = propName.toLowerCase();
      const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      const targetNorm = norm(propName);

      const scopes = [this.section, document];
      for (const scope of scopes) {
        const all = scope.querySelectorAll('input[name^="properties["]');
        for (const inp of all) {
          const name = inp.getAttribute("name") || "";
          if (norm(name).includes(targetNorm)) return inp;
        }
      }
      return null;
    }

    static isEnabled(section) {
      const s = section.querySelector("script[data-fsb-personalizacao-config]");
      if (!s) return false;
      try {
        const c = JSON.parse(s.textContent);
        const handle = c.handle || "";
        const tags = c.tags || [];
        if (PERSONALIZATION_HANDLES.has(handle)) return true;
        return tags.some((t) => normalize(t) === normalize(FEATURE_TAG));
      } catch { return false; }
    }

    _getKit() {
      if (this.cfg?.kit) return this.cfg.kit;
      for (const tag of this.cfg?.tags || []) {
        const l = tag.toLowerCase();
        if (l.startsWith(KIT_TAG_PREFIX)) return l.substring(KIT_TAG_PREFIX.length).trim();
      }
      const h = (this.cfg?.handle || "").toLowerCase();
      if (h.includes("away")) return "away";
      return "home";
    }

    _getTeam() {
      const handle = (this.cfg?.handle || "").toLowerCase();
      for (const [name, t] of Object.entries(TEAMS)) {
        if (name === "brasil") continue;
        if (t.detect(handle)) return t;
      }
      return TEAMS.brasil;
    }
    _getTeamKey() {
      const handle = (this.cfg?.handle || "").toLowerCase();
      for (const [name, t] of Object.entries(TEAMS)) {
        if (name === "brasil") continue;
        if (t.detect(handle)) return name;
      }
      return "brasil";
    }
    _getNameColor() { return this._getTeam().nameColors[this._getKit()] || "#FFFFFF"; }
    _getCostasUrl() {
      /* 1ª opção: mapping específico por productId (foto exclusiva do produto) */
      const pid = this.cfg?.productId;
      const perProduct = this.cfg?.costasPerProduct || {};
      if (pid && perProduct[pid]) return perProduct[pid];

      /* 2ª opção: fallback genérico por time/kit */
      const team = this._getTeam();
      const fallbacks = this.cfg?.costasFallback || {};
      const teamKey = Object.keys(TEAMS).find(k => TEAMS[k] === team);
      return fallbacks[`${teamKey}_${this._getKit()}`] || fallbacks[this._getKit()] || "";
    }
    _numUrl(d) {
      /* Override Corinthians: usa PNGs servidos como asset_url do tema
         (com escudo embaixo + fonte Nike correta). */
      const override = this.cfg?.corinthiansNumOverride?.[this._getKit()];
      if (override && this._getTeam() === TEAMS.corinthians) {
        const idx = parseInt(d, 10);
        if (override[idx]) return override[idx];
      }
      const prefix = this._getTeam().assetPrefix;
      return `${this.cfg?.assetBase || ""}${prefix}${this._getKit()}-num-${d}.png`;
    }
    _letterUrl(ch) {
      /* Override Corinthians home: letras pretas puro (PNGs reprocessados) */
      if (this._getTeam() === TEAMS.corinthians && this._getKit() === "home") {
        const ov = this.cfg?.corinthiansLetterOverrideHome?.[ch];
        if (ov) return ov;
      }
      const prefix = this._getTeam().assetPrefix;
      return `${this.cfg?.assetBase || ""}${prefix}${this._getKit()}-letter-${ch}.png`;
    }
    _getFontFamily() { return this._getTeam().fontFamily; }
    _useLetterPngs() { return this._getTeam().useLetterPngs; }
    _preloadNums() { for (let i = 0; i <= 9; i++) preloadImg(this._numUrl(i)); }

    _findActiveImg() {
      if (!this.carousel) return null;
      const slide =
        this.carousel.querySelector(".product-gallery__carousel-item.is-selected") ||
        this.carousel.querySelector(".product-gallery__carousel-item");
      if (!slide) return null;
      return slide.querySelector("img.product-gallery__image") || slide.querySelector("img");
    }

    /* ── Reorder slides do carousel (igual MC) ──
       Em vez de fazer src swap (que sobrescreve a primeira foto),
       move a slide com alt "costas-personalizar" pra primeira posição. */

    _getSlider() {
      return this.carousel?.querySelector(".flickity-slider") || null;
    }

    _findBackSlide() {
      const slider = this._getSlider();
      if (!slider) return null;
      const slides = slider.querySelectorAll(".product-gallery__carousel-item");
      for (const slide of slides) {
        const img = slide.querySelector("img");
        if (!img) continue;
        const alt = normalize(img.alt || "");
        if (alt.includes(normalize("costas-personalizar"))) return slide;
      }
      return null;
    }

    _createBackSlide() {
      /* Cria uma nova slide com alt "costas-personalizar" clonando a 1a slide
         do carousel pra herdar a estrutura/CSS do tema. */
      const slider = this._getSlider();
      if (!slider) return null;
      const firstSlide = slider.querySelector(".product-gallery__carousel-item");
      if (!firstSlide) return null;
      const clone = firstSlide.cloneNode(true);
      clone.classList.remove("is-selected");
      clone.classList.add("product-gallery__carousel-item--hidden", "fsb-pp-created");
      clone.style.opacity = "0";
      const img = clone.querySelector("img");
      if (img) {
        img.alt = "costas-personalizar";
        img.removeAttribute("srcset");
        img.removeAttribute("data-widths");
      }
      /* Remove qualquer overlay clonado por engano */
      clone.querySelectorAll(".personalizacao-overlay").forEach((el) => el.remove());
      slider.appendChild(clone);
      return clone;
    }

    _saveOriginalOrder() {
      const slider = this._getSlider();
      if (!slider) return;
      this.originalSlideOrder = Array.from(slider.children);
    }

    _forceLoad(img) {
      if (!img) return;
      const ds = img.getAttribute("data-src");
      if (ds && !img.src) img.src = ds.replace(/\{width\}/g, "1000");
      img.classList.remove("lazyload");
      img.classList.add("lazyloaded");
    }

    _setBackSlideImg(slide, url) {
      const img = slide.querySelector("img");
      if (!img || !url) return;
      if (!img.dataset.fsbOrigSrc) img.dataset.fsbOrigSrc = img.src || "";
      if (!img.dataset.fsbOrigDataSrc) img.dataset.fsbOrigDataSrc = img.getAttribute("data-src") || "";
      if (!img.dataset.fsbOrigZoom) img.dataset.fsbOrigZoom = img.getAttribute("data-zoom") || "";
      if (!img.dataset.fsbOrigSrcset) img.dataset.fsbOrigSrcset = img.srcset || "";
      img.src = url;
      img.setAttribute("data-src", url);
      img.setAttribute("data-zoom", url);
      img.srcset = "";
      img.removeAttribute("data-widths");
    }

    _findThumbForSlide(slide) {
      /* Tema usa data-media-id pra linkar thumb à slide. */
      const mediaId = slide?.dataset?.mediaId ||
        slide?.querySelector("[data-media-id]")?.dataset?.mediaId;
      if (!mediaId) {
        /* Fallback: match por mesma img src */
        const src = (slide?.querySelector("img")?.src || "").split("?")[0];
        if (!src) return null;
        const fileBase = src.substring(src.lastIndexOf("/") + 1).split(".")[0];
        const thumbs = document.querySelectorAll(".product-gallery__thumbnail");
        for (const t of thumbs) {
          const ts = (t.getAttribute("href") || "").split("?")[0];
          if (ts && ts.includes(fileBase)) return t;
        }
        return null;
      }
      return document.querySelector(
        `.product-gallery__thumbnail[data-media-id="${mediaId}"]`
      );
    }

    _findThumbByIndex(idx) {
      return document.querySelectorAll(".product-gallery__thumbnail")[idx] || null;
    }

    _hidePreExistingBackThumbs() {
      /* Esconde a(s) thumb(s) costas-personalizar PRÉ-EXISTENTES do produto
         (admin adicionou ela como uma foto) quando Personalizar NÃO está
         ativo. Evita que o user clique numa thumb que mostra camisa em branco
         sem personalização. */
      const slider = this._getSlider();
      if (!slider) return;
      const slides = slider.querySelectorAll(".product-gallery__carousel-item");
      slides.forEach((slide) => {
        const img = slide.querySelector("img");
        const alt = (img?.alt || "").toLowerCase();
        if (!alt.includes("costas-personalizar")) return;
        const mediaId = slide.dataset.mediaId;
        if (!mediaId) return;
        const thumb = document.querySelector(
          `.product-gallery__thumbnail[data-media-id="${mediaId}"]`
        );
        if (thumb && !thumb.dataset.fsbPpDynamic) {
          thumb.dataset.fsbPpHidden = "1";
          thumb.style.display = "none";
        }
      });
    }

    _showPreExistingBackThumbs() {
      document
        .querySelectorAll('.product-gallery__thumbnail[data-fsb-pp-hidden="1"]')
        .forEach((t) => {
          t.style.display = "";
          delete t.dataset.fsbPpHidden;
        });
    }

    _activateBackSlide() {
      const slider = this._getSlider();
      if (!slider) return null;

      const existing = this._findBackSlide();
      const backSlide = existing || this._fallbackToFirstSlide();
      if (!backSlide) return null;

      /* Política de src:
         - Slide pré-existente costas-personalizar → atualiza só se houver
           mapping explícito pro productId (user forneceu foto custom)
         - Sem slide pré-existente (fallback na slide 0): SEMPRE faz src swap
           pra mostrar a foto da personalização (do mapping ou fallback) */
      const pid = this.cfg?.productId;
      const hasExplicitMapping = !!(pid && this.cfg?.costasPerProduct?.[pid]);
      if (!existing || hasExplicitMapping) {
        const url = this._getCostasUrl();
        if (url) this._setBackSlideImg(backSlide, url);
      }

      /* Mostra a thumb costas-personalizar pré-existente (estava escondida) */
      this._showPreExistingBackThumbs();
      /* Atualiza img da thumb pra mostrar mesma foto que vai aparecer ao clicar */
      if (existing) {
        const thumb = this._findThumbForSlide(backSlide);
        const personalizationUrl = this._getCostasUrl();
        if (thumb && personalizationUrl) {
          const tImg = thumb.querySelector("img");
          if (!thumb.dataset.fsbThumbOrigHref) {
            thumb.dataset.fsbThumbOrigHref = thumb.getAttribute("href") || "";
            if (tImg) {
              thumb.dataset.fsbThumbOrigImgSrc = tImg.src || "";
              thumb.dataset.fsbThumbOrigImgDataSrc = tImg.getAttribute("data-src") || "";
            }
          }
          thumb.setAttribute("href", personalizationUrl);
          if (tImg) {
            tImg.src = personalizationUrl;
            tImg.setAttribute("data-src", personalizationUrl);
            tImg.classList.remove("lazyload");
            tImg.classList.add("lazyloaded");
          }
        }
      }

      backSlide.classList.remove("product-gallery__carousel-item--hidden");
      this._forceLoad(backSlide.querySelector("img"));

      /* Salva thumb originalmente ativa pra restaurar quando desativar */
      if (this.originalActiveThumb == null) {
        const cur = document.querySelector(".product-gallery__thumbnail.is-nav-selected:not([data-fsb-pp-dynamic])");
        this.originalActiveThumb = cur || this._findThumbByIndex(0);
      }

      this.swappedImg = backSlide.querySelector("img");
      this.backSlide = backSlide;

      /* Slide pré-existente: clique na thumb dela faz Flickity navegar normalmente.
         Slide dinâmica criada por nós: mostrar via show() manual. */
      if (existing) {
        const thumb = this._findThumbForSlide(backSlide);
        if (thumb) {
          setTimeout(() => thumb.click(), 50);
          setTimeout(() => thumb.click(), 300);
        }
      } else {
        /* Liga listener nas thumbs originais pra esconder slide dinâmica ao navegar */
        this._bindThumbListeners();
        setTimeout(() => this._showDynamicSlide(), 50);
        setTimeout(() => this._showDynamicSlide(), 300);
      }

      return backSlide;
    }

    _fallbackToFirstSlide() {
      /* Cria slide + thumbnail dinâmicos pra não substituir nenhuma foto
         do produto. A primeira foto continua intacta. */
      const slider = this._getSlider();
      if (!slider) return null;
      const firstSlide = slider.querySelector(".product-gallery__carousel-item");
      if (!firstSlide) return null;

      const thumbList = this.section.querySelector(".product-gallery__thumbnail-list");
      const firstThumb = thumbList?.querySelector(".product-gallery__thumbnail");

      const DYNAMIC_ID = "fsbpp-" + Date.now();

      /* === Clone slide === */
      const newSlide = firstSlide.cloneNode(true);
      newSlide.dataset.mediaId = DYNAMIC_ID;
      newSlide.dataset.fsbPpDynamic = "1";
      newSlide.classList.add("fsb-pp-dynamic-slide");
      newSlide.classList.remove("is-selected");
      newSlide.classList.add("product-gallery__carousel-item--hidden");
      newSlide.style.opacity = "0";
      const sImg = newSlide.querySelector("img");
      if (sImg) {
        sImg.alt = "costas-personalizar";
        sImg.removeAttribute("srcset");
        sImg.removeAttribute("data-widths");
        delete sImg.dataset.fsbOrigSrc;
        delete sImg.dataset.fsbOrigDataSrc;
        delete sImg.dataset.fsbOrigZoom;
        delete sImg.dataset.fsbOrigSrcset;
      }
      newSlide.querySelectorAll(".personalizacao-overlay").forEach((el) => el.remove());
      slider.appendChild(newSlide);

      /* === Clone thumb === Atualiza img pra mostrar a foto da personalização
         (mesma que vai aparecer quando user clicar nela) */
      if (firstThumb && thumbList) {
        const newThumb = firstThumb.cloneNode(true);
        newThumb.dataset.mediaId = DYNAMIC_ID;
        newThumb.dataset.fsbPpDynamic = "1";
        newThumb.classList.add("fsb-pp-dynamic-thumb");
        newThumb.classList.remove("is-nav-selected");
        const tImg = newThumb.querySelector("img");
        if (tImg) {
          tImg.alt = "costas-personalizar";
          tImg.removeAttribute("srcset");
        }
        /* Url da foto pra usar na thumb também (mesma da slide) */
        const personalizationUrl = this._getCostasUrl();
        if (personalizationUrl) {
          newThumb.setAttribute("href", personalizationUrl);
          if (tImg) {
            tImg.src = personalizationUrl;
            tImg.setAttribute("data-src", personalizationUrl);
            tImg.classList.remove("lazyload");
            tImg.classList.add("lazyloaded");
          }
        }
        /* Listener manual: ao clicar, navega via show() manual */
        newThumb.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._showDynamicSlide();
        }, true);
        thumbList.appendChild(newThumb);
        this.dynamicThumb = newThumb;
      }

      return newSlide;
    }

    _showDynamicSlide() {
      const slider = this._getSlider();
      if (!slider || !this.backSlide) return;
      /* Esconde todas as slides + força a dinâmica visível */
      slider.querySelectorAll(".product-gallery__carousel-item").forEach((s) => {
        if (s === this.backSlide) return;
        s.classList.remove("is-selected");
        s.classList.add("product-gallery__carousel-item--hidden");
        s.style.opacity = "0";
        s.style.pointerEvents = "none";
      });
      this.backSlide.classList.add("is-selected");
      this.backSlide.classList.remove("product-gallery__carousel-item--hidden");
      this.backSlide.style.opacity = "1";
      this.backSlide.style.pointerEvents = "";
      this.backSlide.style.zIndex = "10";
      /* Sync thumb selected */
      document.querySelectorAll(".product-gallery__thumbnail")
        .forEach((t) => t.classList.remove("is-nav-selected"));
      if (this.dynamicThumb) this.dynamicThumb.classList.add("is-nav-selected");
    }

    _hideDynamicSlide() {
      /* Quando user navega pra outra thumb, esconde a slide dinâmica
         e restaura comportamento normal das demais. */
      const slider = this._getSlider();
      if (!slider) return;
      if (this.backSlide) {
        this.backSlide.classList.remove("is-selected");
        this.backSlide.classList.add("product-gallery__carousel-item--hidden");
        this.backSlide.style.opacity = "0";
        this.backSlide.style.zIndex = "";
      }
      /* Restaura inline styles que setei nas outras slides */
      slider.querySelectorAll(".product-gallery__carousel-item").forEach((s) => {
        if (s === this.backSlide) return;
        s.style.opacity = "";
        s.style.pointerEvents = "";
        s.style.zIndex = "";
      });
    }

    _bindThumbListeners() {
      /* Quando user clica em qualquer thumb que NÃO seja a dinâmica,
         esconde a slide dinâmica pra Flickity poder navegar. */
      if (this.thumbListenersBound) return;
      this.thumbListenersBound = true;
      const onClick = (e) => {
        const thumb = e.target.closest(".product-gallery__thumbnail");
        if (!thumb || thumb.dataset.fsbPpDynamic === "1") return;
        this._hideDynamicSlide();
      };
      document.addEventListener("click", onClick, true);
      this._thumbListenerHandler = onClick;
    }

    _removeDynamicSlideAndThumb() {
      document.querySelectorAll(".fsb-pp-dynamic-slide").forEach((s) => s.remove());
      document.querySelectorAll(".fsb-pp-dynamic-thumb").forEach((t) => t.remove());
      this.dynamicThumb = null;
    }

    _restoreOrder() {
      /* Restaura src original da slide back PRÉ-EXISTENTE (não dinâmica) */
      if (this.backSlide && !this.backSlide.dataset.fsbPpDynamic) {
        const img = this.backSlide.querySelector("img");
        if (img && img.dataset.fsbOrigSrc !== undefined) {
          if (img.dataset.fsbOrigSrc) img.src = img.dataset.fsbOrigSrc;
          if (img.dataset.fsbOrigDataSrc) img.setAttribute("data-src", img.dataset.fsbOrigDataSrc);
          if (img.dataset.fsbOrigZoom) img.setAttribute("data-zoom", img.dataset.fsbOrigZoom);
          img.srcset = img.dataset.fsbOrigSrcset || "";
          delete img.dataset.fsbOrigSrc;
          delete img.dataset.fsbOrigDataSrc;
          delete img.dataset.fsbOrigZoom;
          delete img.dataset.fsbOrigSrcset;
        }

        /* Restaura src original da THUMB pré-existente também (se foi modificada) */
        const thumb = this._findThumbForSlide(this.backSlide);
        if (thumb && thumb.dataset.fsbThumbOrigHref !== undefined) {
          thumb.setAttribute("href", thumb.dataset.fsbThumbOrigHref || "");
          const tImg = thumb.querySelector("img");
          if (tImg && thumb.dataset.fsbThumbOrigImgSrc !== undefined) {
            tImg.src = thumb.dataset.fsbThumbOrigImgSrc || "";
            tImg.setAttribute("data-src", thumb.dataset.fsbThumbOrigImgDataSrc || "");
          }
          delete thumb.dataset.fsbThumbOrigHref;
          delete thumb.dataset.fsbThumbOrigImgSrc;
          delete thumb.dataset.fsbThumbOrigImgDataSrc;
        }
      }

      /* Esconde slide dinâmica antes de remover (limpa estilos das outras) */
      this._hideDynamicSlide();

      /* Remove slide e thumb dinâmicos */
      this._removeDynamicSlideAndThumb();

      /* Esconde a thumb costas-personalizar pré-existente novamente */
      this._hidePreExistingBackThumbs();

      /* Navega de volta pra slide que estava ativa antes de ativar Personalizar */
      const targetThumb = this.originalActiveThumb || this._findThumbByIndex(0);
      if (targetThumb) {
        setTimeout(() => targetThumb.click(), 50);
      }
      this.originalActiveThumb = null;

      this.backSlide = null;
      this.swappedImg = null;
    }

    _syncThumbs() {
      /* Tenta reordenar lista de thumbnails se existir */
      const thumbsList = this.section.querySelector(".product-gallery__thumbnails");
      if (!thumbsList) return;
      const sliderChildren = Array.from(this._getSlider()?.children || []);
      const thumbs = Array.from(thumbsList.querySelectorAll("[data-product-gallery-thumbnail], button, a"));
      if (!thumbs.length) return;
      /* Match thumb por src/href contendo trecho da slide img */
      sliderChildren.forEach((slide) => {
        const img = slide.querySelector("img");
        const src = (img?.src || img?.getAttribute("data-src") || "").split("?")[0];
        if (!src) return;
        const fileBase = src.substring(src.lastIndexOf("/") + 1).split(".")[0].split("_")[0];
        const match = thumbs.find((t) => {
          const tImg = t.querySelector("img");
          const tSrc = (tImg?.src || tImg?.getAttribute("data-src") || "").split("?")[0];
          return tSrc && tSrc.includes(fileBase);
        });
        if (match && match.parentElement === thumbsList) {
          thumbsList.appendChild(match);
        }
      });
    }

    _resetFlickity() {
      /* Tenta múltiplas formas de notificar o Flickity sobre mudança */
      try {
        const inst = window.Flickity?.data?.(this.carousel);
        if (inst) {
          inst.reloadCells?.();
          inst.resize?.();
          inst.select?.(0, false, true);
          return;
        }
      } catch (e) {}
      /* Fallback: dispatch resize pra fade engine re-renderizar */
      window.dispatchEvent(new Event("resize"));
    }

    _getOverlay(slideOrImg) {
      /* Aceita uma slide ou um img — sobe pro container .aspect-ratio dentro da slide */
      const root =
        slideOrImg.classList?.contains("product-gallery__carousel-item") ? slideOrImg : null;
      const container =
        (root ? root.querySelector(".aspect-ratio") : null) ||
        slideOrImg.closest?.(".aspect-ratio") ||
        slideOrImg.parentElement;
      if (!container) return null;
      let ov = container.querySelector(".personalizacao-overlay");
      if (!ov) {
        ov = document.createElement("div");
        ov.className = "personalizacao-overlay";
        ov.setAttribute("data-team", this._getTeamKey());
        ov.setAttribute("data-kit", this._getKit());
        ov.innerHTML = `<div class="pp-overlay__nome"></div><div class="pp-overlay__numero"></div>`;
        const cs = window.getComputedStyle(container);
        if (cs.position === "static") container.style.position = "relative";
        container.appendChild(ov);
      }
      this.overlayDiv = ov;
      return ov;
    }

    _createMobilePreview() {
      /* Insere o preview abaixo dos campos Nome/Número (#aparecer).
         Quando mobile, CSS torna visível; em desktop fica display:none. */
      if (!this.aparecerEl) return null;
      const existing = this.aparecerEl.querySelector(".pp-mobile-preview");
      if (existing) return existing;

      const url = this._getCostasUrl() ||
        this.backSlide?.querySelector("img")?.src || "";
      if (!url) return null;

      const container = document.createElement("div");
      container.className = "pp-mobile-preview";
      container.setAttribute("data-team", this._getTeamKey());
      container.setAttribute("data-kit", this._getKit());
      container.innerHTML = `
        <p class="pp-mobile-preview__title">✍️ Confira a sua camisa personalizada:</p>
        <img class="pp-mobile-preview__img" src="${url}" alt="Preview personalização" />
        <div class="pp-mobile-overlay">
          <div class="pp-mobile-overlay__nome"></div>
          <div class="pp-mobile-overlay__numero"></div>
        </div>
      `;

      this.aparecerEl.appendChild(container);
      return container;
    }

    _removeMobilePreview() {
      this.section.querySelectorAll(".pp-mobile-preview").forEach((el) => el.remove());
      this.mobilePreview = null;
    }

    _renderMobileOverlay(cleanNome, cleanNum) {
      if (!this.mobilePreview) return;
      const nomeEl = this.mobilePreview.querySelector(".pp-mobile-overlay__nome");
      const numEl = this.mobilePreview.querySelector(".pp-mobile-overlay__numero");
      if (!nomeEl || !numEl) return;

      /* NOME */
      if (this._useLetterPngs()) {
        nomeEl.textContent = "";
        nomeEl.style.fontFamily = "";
        nomeEl.style.color = "";
        const frag = document.createDocumentFragment();
        for (const ch of cleanNome) {
          if (ch === " ") {
            const sp = document.createElement("span");
            sp.className = "pp-mobile-overlay__space";
            frag.appendChild(sp);
            continue;
          }
          if (!/[A-Z]/.test(ch)) continue;
          const img = document.createElement("img");
          img.src = this._letterUrl(ch);
          img.alt = ch;
          img.className = "pp-mobile-overlay__letter";
          img.draggable = false;
          frag.appendChild(img);
        }
        nomeEl.appendChild(frag);
      } else {
        nomeEl.textContent = cleanNome;
        nomeEl.style.color = this._getNameColor();
        const ff = this._getFontFamily();
        if (ff) nomeEl.style.fontFamily = `'${ff}', 'Arial Black', sans-serif`;
      }

      /* NÚMERO */
      numEl.innerHTML = "";
      if (cleanNum.length > 0) {
        const frag = document.createDocumentFragment();
        for (const d of cleanNum.split("")) {
          const img = document.createElement("img");
          img.src = this._numUrl(d);
          img.alt = d;
          img.className = "pp-mobile-overlay__digit";
          img.draggable = false;
          frag.appendChild(img);
        }
        numEl.appendChild(frag);
      }
    }

    _renderOverlay(nome, numero) {
      const host = this.backSlide || this.swappedImg;
      if (!host) return;
      const ov = this._getOverlay(host);
      if (!ov) return;
      const nomeEl = ov.querySelector(".pp-overlay__nome");
      const numEl = ov.querySelector(".pp-overlay__numero");

      /* Re-lê do DOM AGORA — não confia em refs cacheadas (podem estar detached) */
      if (!nome) nome = this._findInput("Nome")?.value || "";
      if (!numero) numero = this._findInput("Número")?.value || "";

      const cleanNome = (nome || "").toUpperCase().trim();
      if (this._useLetterPngs()) {
        /* Renderiza nome como PNGs lado a lado (uma img por letra) */
        nomeEl.textContent = "";
        nomeEl.style.fontFamily = "";
        nomeEl.style.color = "";
        const fragNome = document.createDocumentFragment();
        for (const ch of cleanNome) {
          if (ch === " ") {
            const sp = document.createElement("span");
            sp.className = "pp-overlay__space";
            fragNome.appendChild(sp);
            continue;
          }
          if (!/[A-Z]/.test(ch)) continue;
          const img = document.createElement("img");
          img.src = this._letterUrl(ch);
          img.alt = ch;
          img.className = "pp-overlay__letter";
          img.draggable = false;
          fragNome.appendChild(img);
        }
        nomeEl.appendChild(fragNome);
      } else {
        nomeEl.textContent = cleanNome;
        nomeEl.style.color = this._getNameColor();
        const ff = this._getFontFamily();
        if (ff) nomeEl.style.fontFamily = `'${ff}', 'Arial Black', sans-serif`;
      }

      const cleanNum = (numero || "").replace(/\D/g, "").substring(0, 3);
      numEl.innerHTML = "";
      if (cleanNum.length > 0) {
        const frag = document.createDocumentFragment();
        for (const d of cleanNum.split("")) {
          const img = document.createElement("img");
          img.src = this._numUrl(d);
          img.alt = d;
          img.className = "pp-overlay__digit";
          img.draggable = false;
          frag.appendChild(img);
        }
        numEl.appendChild(frag);
      }

      /* Sincroniza mobile preview com mesmos valores */
      this._renderMobileOverlay(cleanNome, cleanNum);
    }

    /* Liga listener em QUALQUER input properties[*] que aparecer no DOM,
       inclusive os que forem injetados/recriados depois (caso o tema
       refresque o form). */
    _bindAllInputs(onType) {
      const bind = (inp) => {
        if (!inp || inp.dataset.fsbBound) return;
        inp.dataset.fsbBound = "1";
        inp.addEventListener("input", onType);
      };
      document.querySelectorAll('input[name^="properties["]').forEach(bind);
    }

    _removeOverlay() {
      this.section.querySelectorAll(".personalizacao-overlay")
        .forEach((el) => el.remove());
      this.overlayDiv = null;
    }

    _activate() {
      if (this.isActive) return;
      this.isActive = true;
      const slide = this._activateBackSlide();
      if (!slide) {
        /* Fallback: tema sem slide back — sai sem mostrar */
        this.isActive = false;
        return;
      }
      this.mobilePreview = this._createMobilePreview();
      this._renderOverlay(this.nomeInput?.value, this.numInput?.value);
    }

    _deactivate() {
      if (!this.isActive) return;
      this.isActive = false;
      this._removeOverlay();
      this._removeMobilePreview();
      this._restoreOrder();
    }

    _isPersonalizando(variant) {
      if (!variant) return false;
      const opts = Array.isArray(variant.options) ? variant.options : [];
      return opts.some((o) => {
        const val = typeof o === "string" ? o : (o?.value ?? o);
        return normalize(val) === "personalizar";
      });
    }

    _readCurrentVariant() {
      const form = this.section.querySelector('form[action*="/cart/add"]');
      if (!form) return null;
      const opts = [];
      form.querySelectorAll('select[name^="options"]').forEach((sel) => {
        if (sel.value?.trim()) opts.push(sel.value);
      });
      form.querySelectorAll('input[name^="options"]:checked').forEach((inp) => {
        if (inp.value?.trim()) opts.push(inp.value);
      });
      return { options: opts };
    }

    init() {
      if (!this.cfg) return;
      this._preloadNums();

      /* Esconde thumbs costas-personalizar pré-existentes desde o início.
         Elas só aparecem quando Personalizar está ativo. */
      this._hidePreExistingBackThumbs();

      const onType = () => {
        if (!this.isActive) return;
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => {
          this._renderOverlay(this.nomeInput?.value, this.numInput?.value);
        }, 80);
      };
      this._bindAllInputs(onType);

      const reattachInputs = () => {
        /* Liga em qualquer properties[*] não-ligado ainda + atualiza refs */
        this._bindAllInputs(onType);
        const newNome = this._findInput("Nome");
        const newNum = this._findInput("Número");
        if (newNome) this.nomeInput = newNome;
        if (newNum) this.numInput = newNum;
      };

      document.addEventListener("variant:changed", (event) => {
        const v = event?.detail?.variant;
        if (this._isPersonalizando(v)) this._activate();
        else this._deactivate();
      });

      const form = this.section.querySelector('form[action*="/cart/add"]');
      if (form) {
        form.querySelectorAll('select[name^="options"], input[name^="options"]')
          .forEach((el) => {
            el.addEventListener("change", () => {
              const v = this._readCurrentVariant();
              if (this._isPersonalizando(v)) this._activate();
              else this._deactivate();
            });
          });
      }

      if (this.aparecerEl && typeof MutationObserver === "function") {
        const isVisible = () => {
          const cs = window.getComputedStyle(this.aparecerEl);
          return cs.display !== "none" && cs.visibility !== "hidden";
        };
        const sync = () => {
          reattachInputs();
          if (isVisible()) this._activate();
          else this._deactivate();
        };
        new MutationObserver(sync).observe(this.aparecerEl, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
        sync();
      } else {
        const initial = this._readCurrentVariant();
        if (this._isPersonalizando(initial)) this._activate();
      }
    }
  }

  function boot() {
    document.querySelectorAll('section[data-section-type="product"]').forEach((sec) => {
      if (!FsbPersonalizacao.isEnabled(sec)) return;
      if (sec.dataset.fsbPpInit) return;
      sec.dataset.fsbPpInit = "1";
      new FsbPersonalizacao(sec).init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
